import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import { IntlProvider } from "react-intl"
import { ConfigProvider } from "antd"
import { useLocation } from "react-router-dom"
import en from "antd/lib/locale/en_US"
import { loadTranslations } from "../../shared/api/translations"
import { getLocale, getProviderLocale } from "../../shared/constants/locale"
import { resolveUiLanguage } from "../../shared/utils/resolve-ui-language"
import { getStoredUiLanguage, setStoredUiLanguage } from "../../shared/utils/ui-language"
import { setTranslationMessages } from "./translations-store"

const LocalizationContext = createContext({
	uiLanguage: "en",
	setUiLanguage: () => { }
})

export const useLocalization = () => useContext(LocalizationContext)

export const Localization = ({ children }) => {
	const location = useLocation()
	const [defaultUiLanguage, setDefaultUiLanguage] = useState(getStoredUiLanguage)
	const [messages, setMessages] = useState(null)
	const [locale, setLocale] = useState(en)
	const latestRequestId = useRef(0)
	const isLoginPath = location.pathname.includes("login")

	const uiLanguage = resolveUiLanguage({
		defaultUiLanguage,
		isLoginPath
	})

	const setUiLanguage = (language) => {
		const normalizedLanguage = language?.toLowerCase() || "en"
		setStoredUiLanguage(normalizedLanguage)
		setDefaultUiLanguage(normalizedLanguage)
	}

	const fetchData = async () => {
		const requestId = latestRequestId.current + 1
		latestRequestId.current = requestId

		const nextMessages = await loadTranslations(uiLanguage)

		if (latestRequestId.current !== requestId) {
			return
		}

		setTranslationMessages(nextMessages)
		setMessages(nextMessages)
		setLocale(getLocale(uiLanguage))
	}

	useEffect(() => {
		fetchData().catch(console.error)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [uiLanguage, isLoginPath])

	const providerLocale = getProviderLocale(uiLanguage)

	return (
		<LocalizationContext.Provider value={{ uiLanguage: defaultUiLanguage, setUiLanguage }}>
			<IntlProvider
				key={providerLocale}
				locale={providerLocale}
				messages={messages}
				onError={() => { }}
			>
				<ConfigProvider locale={locale}>
					{children}
				</ConfigProvider>
			</IntlProvider>
		</LocalizationContext.Provider>
	)
}
